import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Play, Pause, RotateCcw, CheckCircle, AlertCircle, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcessingView = ({ sessionId, onProcessingComplete }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [error, setError] = useState(null);

  const processingSteps = [
    { key: 'initialization', label: 'Initialization', description: 'Preparing images for analysis' },
    { key: 'preprocessing', label: 'Image Preprocessing', description: 'Loading and preprocessing images' },
    { key: 'feature_extraction', label: 'Feature Extraction', description: 'Extracting keypoints and features' },
    { key: 'reconstruction', label: '3D Reconstruction', description: 'Building 3D model from images' },
    { key: 'analysis', label: 'Burn Analysis', description: 'Calculating burn metrics' },
    { key: 'saving', label: 'Saving Results', description: 'Storing analysis results' },
    { key: 'complete', label: 'Complete', description: 'Processing finished successfully' }
  ];

  const startProcessing = async () => {
    try {
      setError(null);
      setIsProcessing(true);

      const response = await axios.post(
        `${API}/sessions/${sessionId}/process`,
        {
          ruler_length_cm: 15, // Default ruler length
          enable_depth_analysis: true,
          enable_3d_reconstruction: true
        }
      );

      if (response.data.status === 'processing_started') {
        // Start polling for status
        pollProcessingStatus();
      }
    } catch (error) {
      console.error('Failed to start processing:', error);
      setError(error.response?.data?.detail || 'Failed to start processing');
      setIsProcessing(false);
    }
  };

  const pollProcessingStatus = () => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}/status`);
        const status = response.data;
        
        setProcessingStatus(status);

        if (status.status === 'complete') {
          clearInterval(interval);
          setIsProcessing(false);
          
          // Get analysis results
          try {
            const analysisResponse = await axios.get(`${API}/sessions/${sessionId}/analysis`);
            if (onProcessingComplete) {
              onProcessingComplete(analysisResponse.data);
            }
          } catch (analysisError) {
            console.error('Failed to fetch analysis results:', analysisError);
          }
        } else if (status.status === 'error') {
          clearInterval(interval);
          setIsProcessing(false);
          setError(status.message || 'Processing failed');
        }
      } catch (error) {
        console.error('Failed to fetch processing status:', error);
        clearInterval(interval);
        setIsProcessing(false);
        setError('Failed to get processing status');
      }
    }, 2000); // Poll every 2 seconds

    // Cleanup interval after 5 minutes maximum
    setTimeout(() => {
      clearInterval(interval);
      if (isProcessing) {
        setError('Processing timeout - please try again');
        setIsProcessing(false);
      }
    }, 300000);
  };

  const getCurrentStepIndex = () => {
    if (!processingStatus) return -1;
    return processingSteps.findIndex(step => step.key === processingStatus.current_step);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Processing Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-500" />
            3D Reconstruction & Analysis Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!isProcessing && !processingStatus && (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ready to Process Images</h3>
                <p className="text-gray-600 mb-6">
                  Start the computer vision pipeline to analyze burn images and generate 3D reconstruction.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">🔬 Processing Pipeline</h4>
                <ul className="text-sm text-blue-800 space-y-1 text-left">
                  <li>• Feature extraction using SIFT/ORB algorithms</li>
                  <li>• Multi-view stereo reconstruction</li>
                  <li>• 3D point cloud generation</li>
                  <li>• Burn area and depth analysis</li>
                  <li>• Clinical metrics calculation (TBSA, volume, healing stage)</li>
                </ul>
              </div>

              <Button 
                onClick={startProcessing}
                size="lg"
                className="px-8"
                data-testid="start-processing-button"
              >
                <Play className="w-5 h-5 mr-2" />
                Start 3D Analysis
              </Button>
            </div>
          )}

          {/* Processing Progress */}
          {(isProcessing || processingStatus) && (
            <div className="space-y-6">
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">
                    {processingStatus?.message || 'Initializing...'}
                  </span>
                  <span className="text-gray-500">
                    {processingStatus?.progress || 0}%
                  </span>
                </div>
                <Progress 
                  value={processingStatus?.progress || 0} 
                  className="w-full h-3"
                />
              </div>

              {/* Processing Steps */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Processing Steps</h4>
                <div className="space-y-3">
                  {processingSteps.map((step, index) => {
                    const isActive = index === currentStepIndex;
                    const isCompleted = index < currentStepIndex;
                    const isPending = index > currentStepIndex;

                    return (
                      <div 
                        key={step.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          isActive ? 'bg-blue-50 border-blue-200' :
                          isCompleted ? 'bg-green-50 border-green-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-blue-500 text-white animate-pulse' :
                          isCompleted ? 'bg-green-500 text-white' :
                          'bg-gray-300 text-gray-600'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <span className="text-sm font-medium">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{step.label}</div>
                          <div className="text-xs text-gray-500">{step.description}</div>
                        </div>
                        {isActive && (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Current Step Details */}
              {processingStatus && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm">
                    <span className="font-medium">Status:</span> {processingStatus.message}
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">Current Step:</span> {processingStatus.current_step}
                  </div>
                  {processingStatus.started_at && (
                    <div className="text-sm mt-1">
                      <span className="font-medium">Started:</span> {new Date(processingStatus.started_at).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Processing Error</span>
              </div>
              <p className="text-red-700 text-sm mt-1">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={() => {
                  setError(null);
                  setProcessingStatus(null);
                  setIsProcessing(false);
                }}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcessingView;